import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import Button from "@/components/Core/Button/Button";
import { RadioGroup, RadioOption } from "@/components/Core/RadioGroup";
import KnowledgeBaseTrigger from "@/components/Shared/KnowledgeBase/KnowledgeBaseTrigger";
import {
	DEFAULT_TIME_REPORT_GRANULARITY,
	TimeReportGranularity,
} from "@/models/Organisation";
import { useOrganisation, useUpdateOrganisation } from "@/networking/organisation.hooks";
import { SettingsLeaf } from "./SettingsLeafLayout";

/**
 * "Tidrapportering" — how finely consultants can report start/end time in the app.
 *
 * The setting governs the app's time picker only. The dashboard's own
 * reported/billed inputs stay free-text HH:MM so admins can correct exact times,
 * and nothing downstream quantises — salary, OB and invoicing all work off
 * minute-exact diffs already.
 *
 * There is no backfill: an org without `timeReportSettings` behaves as 15 min,
 * which is what every org did before the field existed.
 */
const GRANULARITY_OPTIONS: RadioOption[] = [
	{ value: "5", label: "5 minuter", hint: "Konsulten kan rapportera t.ex. 07:05 och 07:10." },
	{ value: "15", label: "15 minuter", hint: "Konsulten kan rapportera hel, kvart, halv." },
];

const TidrapporteringPage = () => {
	const { t } = useTranslation();
	const { data: organisation } = useOrganisation();
	const { mutate: updateOrganisation, isLoading: isSavingOrganisation } = useUpdateOrganisation();

	const saved =
		organisation?.timeReportSettings?.granularityMinutes ?? DEFAULT_TIME_REPORT_GRANULARITY;

	const [granularityDraft, setGranularityDraft] =
		useState<TimeReportGranularity>(DEFAULT_TIME_REPORT_GRANULARITY);

	useEffect(() => {
		setGranularityDraft(saved);
	}, [saved]);

	const hasUnsavedChanges = granularityDraft !== saved;

	// The whole nested object is replaced on save (the org PUT $sets top-level
	// fields), so always send it complete rather than patching into it.
	const saveGranularity = () =>
		updateOrganisation({ timeReportSettings: { granularityMinutes: granularityDraft } });

	return (
		<SettingsLeaf
			title={
				<span className="inline-flex items-center gap-1.5">
					Tidrapportering
					<KnowledgeBaseTrigger tag="time-report-granularity" />
				</span>
			}
			description="Styr hur exakt konsulterna kan ange start- och sluttid i appen. Påverkar inte tider du själv skriver in här i dashboarden."
		>
			<RadioGroup
				label="Intervall vid tidrapportering"
				orientation="vertical"
				options={GRANULARITY_OPTIONS}
				value={String(granularityDraft)}
				onChange={(value: string) => setGranularityDraft(Number(value) as TimeReportGranularity)}
				skipTranslation
			/>

			<div>
				<Button
					onClick={saveGranularity}
					disabled={!hasUnsavedChanges || isSavingOrganisation}
					height="37px"
					fontSize="s"
				>
					{t("SAVE_CHANGES")}
				</Button>
			</div>
		</SettingsLeaf>
	);
};

export default TidrapporteringPage;
